import { Droppable } from "@hello-pangea/dnd";
import { Delete, Edit, MoreHoriz } from "@mui/icons-material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  Box,
  CircularProgress,
  IconButton,
  keyframes,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import {
  DealAttachmentFolderRead,
  DealAttachmentRead,
} from "types/api/deal/deal_attachment";

const pulseBackground = keyframes`
  0% {
    background-color: #3498db;
  }
  50% {
    background-color: #5dade2;
  }
  100% {
    background-color: #3498db;
  }
`;

export const FoldersList: React.FC<{
  folders: DealAttachmentFolderRead[];
  attachments: DealAttachmentRead[];
  loading: boolean;
  handleFolderClick: (id: string | null) => void;
  isDragging: boolean;
  readOnly?: boolean;
  selectedFolderId: string | null;
  handleFolderDelete: (id: string) => void;
  setSelectedItem: (item: { id: string; name: string }) => void;
  handleOpenModal: (
    mode: "create-folder" | "rename-folder" | "rename-file",
    item?: { id: string; name: string }
  ) => void;
}> = ({
  folders,
  attachments,
  loading,
  handleFolderClick,
  handleFolderDelete,
  handleOpenModal,
  selectedFolderId,
  setSelectedItem,
  isDragging,
  readOnly = false,
}) => {
  const FOLDER_NAME_LENGTH_LIMIT = 18;

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      {[{ id: null, name: "All Files", deal_id: 0 }, ...folders].map(
        (folder, index) => {
          const isAllFiles = folder.id === null;
          const filesInFolder = isAllFiles
            ? attachments
            : attachments.filter((a) => a.folder_id === folder.id);
          return (
            <Droppable
              key={index}
              droppableId={isAllFiles ? "All Files" : folder.id}
              isDropDisabled={readOnly || isAllFiles}
            >
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  sx={{
                    p: 2,
                    borderRadius: 0,
                    cursor: "pointer",
                    transition: "opacity 0.2s ease, border-color 0.2s ease",
                    ...(snapshot.isDraggingOver &&
                      !isAllFiles && {
                        backgroundColor: "primary.main",
                        animation: `${pulseBackground} 1s infinite ease-in-out`,
                        color: "white",
                      }),
                    opacity:
                      isDragging && !snapshot.isDraggingOver && !isAllFiles
                        ? 0.5
                        : 1,
                    ...(selectedFolderId === folder.id &&
                    !snapshot.isDraggingOver
                      ? {
                          backgroundColor: "#EBEDEE",
                        }
                      : {}),
                  }}
                  onClick={() => handleFolderClick(folder.id)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box style={{ display: "flex", alignItems: "center" }}>
                      {!isAllFiles && (
                        <FolderOpenIcon
                          sx={{
                            color:
                              snapshot.isDraggingOver && !isAllFiles
                                ? "white"
                                : "gray.300",
                            mr: 1.5,
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: 15,
                          fontWeight: 600,
                        }}
                      >
                        {folder.name.length > FOLDER_NAME_LENGTH_LIMIT
                          ? `${folder.name.slice(0, FOLDER_NAME_LENGTH_LIMIT)}...`
                          : folder.name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        mx: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="h5"
                        color={
                          snapshot.isDraggingOver && !isAllFiles
                            ? "white"
                            : "textPrimary.main"
                        }
                      >
                        {filesInFolder.length}
                      </Typography>
                    </Box>
                  </Box>

                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          );
        }
      )}
    </>
  );
};
